import * as React from "react";
import { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import CssBaseline from "@mui/material/CssBaseline";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Dialog from "@mui/material/Dialog";
import Collapse from "@mui/material/Collapse";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CloseIcon from "@mui/icons-material/Close";
import Alert from "@mui/material/Alert";
import { styled } from "@mui/material/styles";
import ButtonGroup from "@mui/material/ButtonGroup";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import Header from "./Header";
import Footer from "./Footer";
import Logo from "./Logo";
import NoAccessPage from "./NoAccessPage";
import { useTheme } from "@mui/material/styles";
import Brightness1RoundedIcon from "@mui/icons-material/Brightness1Rounded";
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  useMediaQuery,
} from "@mui/material";
import { useCallback } from "react";
import { sideBarData, sidebarSections } from "./Sidebar/sidebarConstant.js";
import { borderColors, ColorsConstants } from "../cssUtils/constants.js";
import SidebarIcon from "../common/SidebarButton";
import { SETTINGS_DASHBOARD_ENDPOINT } from "../config/urls.js";
import axios from "axios";
import { useAuth } from "../hooks/AuthProvider.js";
import { ChevronRight } from "@mui/icons-material";
import { confirmDiscardChanges } from "../common/unsaved-changes";

// Fixed drawer width - constant regardless of screen size
const DRAWER_WIDTH = 229;

const tenant = localStorage.getItem("tenant");

// Sections that should always be visible in sidebar (with lock icon)
const ALWAYS_VISIBLE_SECTIONS = ["commerce", "agentic_ai", "drip_marketing", "rcs_bot_create", "chatbot_configuration", "campaigns", "analyticsMenu", "voice"];

// Helper: check permission(s) — supports both a single string and an array (any match = granted)
const checkPermission = (perm, userPerms) => {
  if (!perm) return true;
  if (Array.isArray(perm)) return perm.some((p) => userPerms.includes(p));
  return userPerms.includes(perm);
};

export default function MiniDrawer(props) {
  const permissionsFromStorage = JSON.parse(
    localStorage.getItem("permissions") || "[]",
  );
  const { getValidToken } = useAuth();
  console.log(process.env.NODE_ENV, "");
  const isProduction = process.env.NODE_ENV === "production";

  const appsFromStorage = JSON.parse(localStorage.getItem("apps") || "[]");

  console.log("app from storage ********", appsFromStorage);
  const featuresFromStorage = JSON.parse(
    localStorage.getItem("features") || "[]",
  );
  const [selectedComponentKey, setSelectedComponentKey] = useState(
    localStorage.getItem("activePage") || "dashboard",
  ); // Keep track of the selected component key
  const [pageParams, setPageParams] = useState(null);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerPinned, setDrawerPinned] = useState(false);
  const [hoveredSection, setHoveredSection] = useState(null);
  const [activeSection, setActiveSection] = useState(
    () => localStorage.getItem("activeSection") || "dashboard",
  );
  const [businessNumber, setBusinessNumber] = useState("");
  // Get user permissions from localStorage
  const [userApps, setUserApps] = useState(appsFromStorage);
  const [userFeatures, setUserFeatures] = useState(featuresFromStorage);
  const [userPermissions, setUserPermissions] = useState(
    permissionsFromStorage,
  );
  const [activeGroup, setActiveGroup] = useState("");
  // Get user role
  const userRole = JSON.parse(localStorage.getItem("user"))?.role || "";
  // The login response's `user` has no role; it is returned (and stored) separately as "role"
  // (see AuthProvider.getRole). Access rules that depend on the role must use this one.
  const accountRole = localStorage.getItem("role") || "";
  const [expandedNested, setExpandedNested] = useState({});

  // Toggle nested items
  const toggleNestedItems = (itemKey) => {
    setExpandedNested((prev) => ({
      ...prev,
      [itemKey]: !prev[itemKey],
    }));
  };

  // Filter out sections that are not in the apps list, but keep ALWAYS_VISIBLE_SECTIONS always visible
  const updatedSidebarSections = Object.fromEntries(
    Object.entries(sidebarSections).filter(([key, value]) => {
      // Always show ALWAYS_VISIBLE_SECTIONS sections except when plan is elite / pro (mf plans)
      
      const plan = localStorage.getItem("plan")
        
      if (
        ALWAYS_VISIBLE_SECTIONS.includes(key) &&
        (key === "analyticsMenu" || !(["elite", "pro"].includes(plan)))
      ) {
        return true;
      }

      // For other sections, filter based on user apps
      if (!userApps.includes(value.app)) return false;

      // A section that opts in with `enforceAccess` (e.g. Members) is also limited to its roles.
      if (value.enforceAccess && value.roles?.length && !value.roles.includes(accountRole)) return false;

      return true;
    }),
  );

  let features = ["custom", "reports"];
  const planFeatures = JSON.parse(localStorage.getItem("features")) || [];
  features = [...features, ...planFeatures];

  const tenant = localStorage.getItem("tenant");

  const formatSectionTitle = (section) => {
    if (!section) return "";

    // Special case for specific sections
    if (section === "finance360") return "Finance 360";
    if (section === "whatsappChat") return "WA Chat";
    if (section === "whatsappCampaign") return "WA Marketing";
    if (section === "analytics") return "WA Analytics";
    if (section === "whatsappTemplates") return "WA Templates";
    if (section === "commerce") return "WA Commerce";

    // Default formatting for other sections
    return section
      .replace(/([A-Z])/g, " $1") // Insert space before capital letters
      .replace(/^./, (str) => str.toUpperCase()); // Capitalize first letter
  };

  // Updated function to check if the user has access based on roles, apps, features and permissions
  const isAccessible = (key) => {
    // Handle direct section keys - they are always accessible if the section exists
    if (key.startsWith("section_direct_")) {
      const sectionId = key.replace("section_direct_", "");
      const section = updatedSidebarSections[sectionId];
      if (!section) return false;

      // Check permissions from the section
      // If section has permission, check if user has it
      // If no permission defined, it's accessible
      if (section.permission) {
        return checkPermission(section.permission, userPermissions);
      }
      return true; // No permission required
    }

    const menuItem = sideBarData[key];
    if (!menuItem) return false;

    // Check if user has required permissions (if the item has a permission property)
    // If permission is null, no specific permission is required
    const permissionCheck =
      !menuItem.permission || checkPermission(menuItem.permission, userPermissions);

    return permissionCheck;
  };

  // Check if a menu item should show a lock (for any item that user doesn't have permission to access)
  const shouldShowLock = useCallback((key) => {
    const menuItem = sideBarData[key];
    if (!menuItem) return false;
    
    // Check if user has permission for this item
    return !isAccessible(key);
  }, [isAccessible]);

  // Check if a section is locked (for commerce or agentic_ai)
  const isSectionLocked = useCallback((sectionId) => {
    if (!ALWAYS_VISIBLE_SECTIONS.includes(sectionId)) return false;
    
    const section = updatedSidebarSections[sectionId];
    if (!section) return false;
    
    // Check if user has permission for this section
    const hasPermission = !section.permission || checkPermission(section.permission, userPermissions);
    return !hasPermission;
  }, [userPermissions]);

  // Check if a section should be visible in the sidebar
  const shouldShowSection = useCallback((sectionId) => {
    const section = updatedSidebarSections[sectionId];
    if (!section) return false;

    // Always show commerce and agentic_ai sections
    if (ALWAYS_VISIBLE_SECTIONS.includes(sectionId)) {
      return true;
    }

    // For other sections, check if user has permission or if there are any accessible items
    const hasPermission = !section.permission || checkPermission(section.permission, userPermissions);
    if (hasPermission) return true;

    // Check if there are any accessible menu items in this section
    const sectionKeys = getSectionMenuKeys(sectionId);
    return sectionKeys.some(key => !sideBarData[key].hidden && isAccessible(key));
  }, [userPermissions, isAccessible]);

  // Check if a section has any accessible menu items
  const hasAccessibleMenuItems = useCallback(
    (sectionId) => {
      const sectionKeys = getSectionMenuKeys(sectionId);
      return sectionKeys.some(
        (key) => !sideBarData[key].hidden && isAccessible(key),
      );
    },
    [isAccessible],
  );

  // Get section menu keys based on sectionId
  const getSectionMenuKeys = useCallback((sectionId) => {
    return Object.keys(sideBarData).filter((key) => {
      const menuItem = sideBarData[key];

      // Comprehensive mapping for ALL sections
      if (sectionId === "dashboard") return menuItem.app === "dashboard";
      if (sectionId === "quiz")
        return key === "otm.quiz_dashboard";
      if (sectionId === "pharmacy")
        return key === "otm.pharmacy_dashboard";
      if (sectionId === "doorSolutions")
        return menuItem.app === "door_solutions";
      if (sectionId === "finance360") return menuItem.app === "finance360";
      if (sectionId === "commerce") return menuItem.app === "commerce";
      if (sectionId === "customers") return menuItem.app === "customers";
      if (sectionId === "whatsappChat") return menuItem.app === "chat";
      if (sectionId === "whatsappCampaign") return menuItem.app === "campaigns";
      if (sectionId === "agentic_ai") return menuItem.app === "agentic_ai";
      if (sectionId === "billing") return menuItem.app === "billing";
      if (sectionId === "rcs_bot_create")
        return menuItem.app === "rcs_bot_create";
      if (sectionId === "logs") return menuItem.app === "logs";
      if (sectionId === "ctwa") return menuItem.app === "ctwa";
      if (sectionId === "settings") return menuItem.app === "settings";
      if (sectionId === "main") return menuItem.app === "main";
      if (sectionId === "reports") return menuItem.app === "reports";
      if (sectionId === "kalnirnay") return menuItem.app === "kalnirnay";
      if (sectionId === "appointmentSystem")
        return menuItem.app === "appointment_system";
      if (sectionId === "insugo")
        return menuItem.app === "insugo";
      if (sectionId === "climatronics")
        return menuItem.app === "climatronics";
      if (sectionId === "botbridge")
        return menuItem.app === "botbridge";
      if (sectionId === "cent_bank") return menuItem.app === "cent_bank";
      if (sectionId === "mutual_fund_distributor")
        return menuItem.app === "mutual_fund_distributor"
      if (sectionId === "chatbot_configuration")
        return menuItem.app === "chatbot_configuration";
      if (sectionId === "channels") return menuItem.app === "channels";
      if (sectionId === "waba_onboarding")
        return menuItem.app === "waba_onboarding";
      if (sectionId === "integration") return menuItem.app === "integration";
      if (sectionId === "bussiness_profile")
        return menuItem.app === "bussiness_profile";
      if (sectionId === "users") return menuItem.app === "users";
      if (sectionId === "tools") return menuItem.app === "tools";
      if (sectionId === "departments") return menuItem.app === "departments";
      if (sectionId === "audit_trial") return menuItem.app === "audit_trial";
      if (sectionId === "rcs_bot_create")
        return menuItem.app === "rcs_bot_create";
      if (sectionId === "role_permission")
        return menuItem.app === "role_permission";
      if (sectionId === "analytics") return menuItem.app === "analytics";
      if (sectionId === "lead_generation") return menuItem.app === "lead_generation";
        return menuItem.app === sectionId
    });
  }, []);

  const getFirstAccessibleMenuItem = useCallback(
    (sectionId) => {
      // Get all keys that belong to this section
      const sectionKeys = getSectionMenuKeys(sectionId);

      // Filter out hidden items and find the first accessible one
      const firstAccessibleKey = sectionKeys.find(
        (key) => !sideBarData[key].hidden && isAccessible(key),
      );

      return firstAccessibleKey;
    },
    [isAccessible, getSectionMenuKeys],
  );

  const getAccessRestrictionReason = (key) => {
    const menuItem = sideBarData[key];
    if (!menuItem) return "Unknown item";

    if (!userApps.includes(menuItem.app)) return "App not accessible";

    if (!menuItem.roles.includes(userRole)) return "Role restriction";

    if (menuItem.feature && !userFeatures.includes(menuItem.feature))
      return "Feature not enabled";

    if (menuItem.permission && !checkPermission(menuItem.permission, userPermissions))
      return `Missing permission: ${menuItem.permission}`;

    return "Access restricted";
  };

  // Handle browser navigation (back/forward)
  const handlePopState = useCallback((event) => {
    if (!event.state?.componentKey) {
      return;
    }

    const { componentKey, params } = event.state;

    setSelectedComponentKey(componentKey);
    setPageParams(params || null);

    // Handle direct sections in popstate
    if (componentKey.startsWith("section_direct_")) {
      const sectionId = componentKey.replace("section_direct_", "");
      setActiveSection(sectionId);
      setActiveGroup("");
      setExpandedNested({});
    }

    // Keep storage in sync for reload
    localStorage.setItem("activePage", componentKey);
    if (params) {
      localStorage.setItem("activePageParams", JSON.stringify(params));
    } else {
      localStorage.removeItem("activePageParams");
    }
  }, []);

  const navigate = useCallback(
    (key, params = {}, { replace = false } = {}) => {
      // For direct sections, always consider them accessible
      let pageKey = key;

      if (!key.startsWith("section_direct_")) {
        // Check if this is a locked item (any item without permission)
        if (shouldShowLock(key)) {
          // Navigate to noAccess with the item info
          const menuItem = sideBarData[key];
          const sectionId = getSectionIdFromApp(menuItem.app);
          pageKey = "noAccess";
          params = {
            ...params,
            lockedSection: sectionId,
            lockedItem: key,
            itemTitle: menuItem.title,
            sectionTitle: sidebarSections[sectionId]?.title || formatSectionTitle(sectionId),
          };
        } else {
          pageKey = isAccessible(key) ? key : "noAccess";
        }
      }

      const state = { componentKey: pageKey, params };

      // Update React state (UI source of truth)
      setSelectedComponentKey(pageKey);

      // Persist ONLY for reload
      localStorage.setItem("activePage", pageKey);
      if (Object.keys(params).length) {
        localStorage.setItem("activePageParams", JSON.stringify(params));
      } else {
        localStorage.removeItem("activePageParams");
      }

      // Clear active group and nested expanded when navigating to a direct section
      if (pageKey.startsWith("section_direct_")) {
        setActiveGroup("");
        setExpandedNested({});
      }

      // Update browser history
      if (replace) {
        window.history.replaceState(state, "", window.location.pathname);
      } else {
        window.history.pushState(state, "", window.location.pathname);
      }
    },
    [isAccessible, shouldShowLock],
  );

  // Helper function to get sectionId from app name
  const getSectionIdFromApp = (app) => {
    const appToSectionMap = {
      dashboard: "dashboard",
      chat: "whatsappChat",
      campaigns: "whatsappCampaign",
      agentic_ai: "agentic_ai",
      whatsapp_templates: "whatsappTemplates",
      customers: "customers",
      rcs_bot_create: "rcs_bot_create",
      appointment_system: "appointmentSystem",
      insugo: "insugo",
      climatronics: "climatronics",
      botbridge: "botbridge",
      mutual_fund_distributor: "mutual_fund_distributor",
      cent_bank: "cent_bank",
      door_solutions: "doorSolutions",
      integrations: "integrations",
      kalnirnay: "kalnirnay",
      onetouchindiamarketinglive: "quiz",
      finance360: "finance360",
      commerce: "commerce",
      billing: "billing",
      settings: "settings",
      logs: "logs",
      ctwa: "ctwa",
      analytics: "analytics",
      lead_generation: "lead_generation",
    };
    return appToSectionMap[app] || app;
  };

  const fetchSettingsData = async () => {
    try {
      const token = await getValidToken();
      const headers = {
        Authorization: `Bearer ${token}`,
        "X-Tenant-ID": tenant,
      };
      const response = await axios.get(SETTINGS_DASHBOARD_ENDPOINT, {
        headers,
      });
      const data = response?.data;
      console.log("Settings Response:", data);
      setBusinessNumber(data?.business_wa_number);
    } catch (error) {
      console.error("Error fetching about:", error);
      // Don't show error for empty about
    }
  };

  useEffect(() => {
    fetchSettingsData();
    const stored = localStorage.getItem("activeSection");
    if (stored) setActiveSection(stored);
  }, []);

  // Event listener for browser navigation
  useEffect(() => {
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [handlePopState]);

  // Handle sidebar navigation
  const handleListItemClick = useCallback(
    (key, params = {}) => {
      // Stay on the page if the user wants to keep their unsaved form changes
      if (!confirmDiscardChanges()) return;

      const menuItem = sideBarData[key];
      
      // Clear any existing active group first
      setActiveGroup("");
      
      // Determine and set the active section based on the clicked item
      if (menuItem) {
        const appToSectionMap = {
          dashboard: "dashboard",
          chat: "whatsappChat",
          campaigns: "whatsappCampaign",
          agentic_ai: "agentic_ai",
          whatsapp_templates: "whatsappTemplates",
          customers: "customers",
          rcs_bot_create: "rcs_bot_create",
          appointment_system: "appointmentSystem",
          insugo: "insugo",
          climatronics: "climatronics",
          botbridge: "botbridge",
          mutual_fund_distributor: "mutual_fund_distributor",
          cent_bank: "cent_bank",
          door_solutions: "doorSolutions",
          integrations: "integrations",
          kalnirnay: "kalnirnay",
          onetouchindiamarketinglive: "quiz",
          finance360: "finance360",
          commerce: "commerce",
          billing: "billing",
          settings: "settings",
          logs: "logs",
          ctwa: "ctwa",
          analytics: "analytics",
          lead_generation: "lead_generation",
        };

        const newSection = appToSectionMap[menuItem.app] || menuItem.app;
        
        // Update active section state immediately
        setActiveSection(newSection);
        localStorage.setItem("activeSection", newSection);
      }

      // Then navigate
      navigate(key, params);
      setDrawerOpen(false);
      setPageParams(params);

      // Store group information for hidden items
      if (menuItem?.group) {
        setActiveGroup(menuItem.group);
      }

      // Auto-expand parent sections when a child is clicked
      if (menuItem?.parent) {
        setExpandedNested((prev) => ({
          ...prev,
          [menuItem.parent]: true,
        }));
      }
    },
    [navigate],
  );

  // Initial load useEffect - FIXED
  useEffect(() => {
    let key = localStorage.getItem("activePage") || "dashboard";

    // A stored page that opts in with `enforceAccess` (e.g. Members) falls back to the
    // dashboard when this tenant or role no longer has it, so no data loads for them.
    const storedSection = key.startsWith("section_direct_")
      ? sidebarSections[key.replace("section_direct_", "")]
      : null;
    if (storedSection?.enforceAccess) {
      const apps = JSON.parse(localStorage.getItem("apps") || "[]");
      const role = localStorage.getItem("role") || "";
      if (!apps.includes(storedSection.app) || (storedSection.roles?.length && !storedSection.roles.includes(role))) {
        key = "dashboard";
        localStorage.setItem("activePage", key);
        localStorage.removeItem("activePageParams");
      }
    }

    const params = JSON.parse(localStorage.getItem("activePageParams") || "{}");

    // Check if the key is a direct section
    if (key.startsWith("section_direct_")) {
      const sectionId = key.replace("section_direct_", "");
      setActiveSection(sectionId);
      setActiveGroup("");
      setExpandedNested({});
    } else {
      // For normal menu items, check if we need to expand their section
      const menuItem = sideBarData[key];
      if (menuItem) {
        // Determine which section this item belongs to
        let sectionId = getSectionIdFromApp(menuItem.app);

        if (sectionId) {
          setActiveSection(sectionId);
          // Auto-expand the section
          setExpandedSections((prev) => ({
            ...prev,
            [sectionId]: true,
          }));
        }

        // Auto-expand parent if this is a child item
        if (menuItem.parent) {
          setExpandedNested((prev) => ({
            ...prev,
            [menuItem.parent]: true,
          }));
        }

        // Set active group if any
        if (menuItem.group) {
          setActiveGroup(menuItem.group);
        }
      }
    }

    setSelectedComponentKey(key);
    setPageParams(Object.keys(params).length ? params : null);

    window.history.replaceState(
      { componentKey: key, params },
      "",
      window.location.pathname,
    );
  }, []);

  // Update the component rendering logic - FIXED
  const getComponentToRender = () => {
    // Check if this is a direct section key
    if (
      selectedComponentKey &&
      selectedComponentKey.startsWith("section_direct_")
    ) {
      const sectionId = selectedComponentKey.replace("section_direct_", "");
      const section = updatedSidebarSections[sectionId];
      
      // For direct sections, check if user has permission to access the section
      if (section) {
        const hasSectionPermission = !section.permission || checkPermission(section.permission, userPermissions);
        if (!hasSectionPermission) {
          return NoAccessPage;
        }
        return section.component;
      }
    }

    // For normal menu items, check accessibility
    if (!isAccessible(selectedComponentKey)) {
      return NoAccessPage;
    }

    // Return the component for the selected key
    return sideBarData[selectedComponentKey]?.component ?? NoAccessPage;
  };

  const ComponentToRender = getComponentToRender();

  const productionExcepted = ["chatbot_configuration"];

  // onMainDrawerClick - FIXED
  const onMainDrawerClick = (isActive, sectionId) => {
    if (isActive) {
      // If clicking on already active section, do nothing
      return;
    }

    const section = updatedSidebarSections[sectionId];

    // Check if this is a locked section (commerce or agentic_ai without permission)
    const isLockedSection = isSectionLocked(sectionId);

    if (isLockedSection) {
      // Navigate to no access page for locked section
      navigate("noAccess", {
        lockedSection: sectionId,
        sectionTitle: section?.title || formatSectionTitle(sectionId),
      });
      return;
    }

    // For sections with direct components, check permission before navigating
    if (section?.component) {
      const hasSectionPermission = !section.permission || checkPermission(section.permission, userPermissions);
      if (!hasSectionPermission) {
        navigate("noAccess", {
          lockedSection: sectionId,
          sectionTitle: section?.title || formatSectionTitle(sectionId),
        });
        return;
      }
    }

    // Clear any active group when changing sections
    setActiveGroup("");

    // Clear expanded nested states
    setExpandedNested({});

    setActiveSection(sectionId);
    localStorage.setItem("activeSection", sectionId);

    const firstMenuItem = getFirstAccessibleMenuItem(sectionId);
    if (firstMenuItem) {
      handleListItemClick(firstMenuItem);
    } else {
      navigate("noAccess");
    }
  };

  const [expandedSections, setExpandedSections] = useState({});

  // Toggle section expansion
  const toggleSection = (sectionId) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  // Auto-expand section when a subitem is active
  useEffect(() => {
    // Skip if selectedComponentKey is a direct section
    if (selectedComponentKey?.startsWith("section_direct_")) {
      return;
    }

    // Find which section the current active page belongs to
    const activeMenuItem = sideBarData[selectedComponentKey];

    // Also check if there's an active group item
    let activeFromGroup = null;
    if (activeGroup) {
      activeFromGroup = Object.values(sideBarData).find(
        (item) => item.group === activeGroup,
      );
    }

    const menuItemToUse = activeMenuItem || activeFromGroup;

    if (menuItemToUse) {
      let activeSectionId = getSectionIdFromApp(menuItemToUse.app);

      if (activeSectionId) {
        setActiveSection(activeSectionId);
        // Auto-expand the section
        setExpandedSections((prev) => ({
          ...prev,
          [activeSectionId]: true,
        }));
      }
    }
  }, [selectedComponentKey, activeGroup]);

  console.log("updated", updatedSidebarSections);

  return (
    <Box sx={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      <CssBaseline />

      {/* Sidebar - Fixed width drawer */}
      <Drawer
        variant="permanent"
        elevation={0}
        sx={{
          flexShrink: 0,
          whiteSpace: "nowrap",
          width: DRAWER_WIDTH,
          [`& .MuiDrawer-paper`]: {
            width: DRAWER_WIDTH,
            overflowX: "hidden",
            overflowY: "auto",
            zIndex: 1200,
            height: "100vh",
            pt: 0,
            position: "relative",
            backgroundColor: ColorsConstants.primaryWhite,
            borderRight: "none",
            boxShadow: "4px 0px 10px rgba(0, 0, 0, 0.05)",
            "&::-webkit-scrollbar": {
              width: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#E2E8F0",
              borderRadius: "4px",
            },
          },
        }}
      >
        {/* Dynamic Branding Header Section */}
        <Box
          sx={{
            px: 2,
            pt: "10px",
            pb: "10px",
            borderBottom: "1px solid #E9E9E9",
            borderRight: "1px solid #E9E9E9",
            backgroundColor: "#FFFFFF",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 2,
          }}
        >
          <Logo />
        </Box>

        <Box sx={{ overflow: "auto", px: 1.5 }}>
          {/* Define section groups */}
          {[
            {
              title: "DASHBOARD",
              sections: ["dashboard"],
            },
            {
              title: "CUSTOM",
              sections: [
                "main",
                "reports",
                "survey_reports",
                "kalnirnay",
                "appointmentSystem",
                "insugo",
                "climatronics",
                "mutual_fund_distributor",
                "cent_bank",
                "doorSolutions",
                "quiz",
                "finance360",
                "kalanjay",
                "botbridge",
                "magento",
                "pharmacy",
                "members"
              ],
            },
            {
              title: "MODULE",
              sections: [
                "whatsappChat",
                "whatsappCampaign",
                "analyticsMenu",
                "agentic_ai",
                "customers",
                "commerce",
                "analytics",
                "ctwa",
                "lead_generation"
              ],
            },
            {
              title: "CONFIGURATION",
              sections: [
                "chatbot_configuration",
                "channels",
                "waba_onboarding",
                "rcs_bot_create",
                "message_template",
                "integration",
                "whatsapp_voice_config",
                "instagram_flow",
                "billing",
                "settings",
              ],
            },
            {
              title: "MANAGE",
              sections: [
                "bussiness_profile",
                "users",
                "tools",
                "departments",
                "audit_trial",
                "role_permission",
              ],
            },
            {
              title: "TRACKING",
              sections: ["logs"],
            },
          ].map((group) => {
            // Filter sections that exist in updatedSidebarSections
            const availableSections = group.sections.filter(
              (sectionId) => updatedSidebarSections[sectionId],
            );

            if (availableSections.length === 0) return null;

            return (
              <Box key={group.title} sx={{ mb: 3 }}>
                {/* Group Title */}
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#8C8C8C",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    mb: 1,
                    px: 1,
                  }}
                >
                  {group.title}
                </Typography>

                {/* Render sections in this group */}
                {availableSections.map((sectionId) => {
                  const section = updatedSidebarSections[sectionId];

                  // Check if this is a locked section (commerce or agentic_ai without permission)
                  const isLockedSection = isSectionLocked(sectionId);

                  const isActive = activeSection === sectionId;

                  // Get all menu items for this section
                  const sectionMenuKeys = getSectionMenuKeys(sectionId);

                  const sectionMenuItems = sectionMenuKeys
                    .filter((key) => {
                      const menuItem = sideBarData[key];
                      
                      // For locked sections (commerce/agentic_ai), show all items (they will have lock icons)
                      if (isLockedSection) {
                        return !menuItem.hidden;
                      }
                      
                      // For unlocked sections, show items that are not hidden
                      return !menuItem.hidden;
                    });

                  const hasSubItems = sectionMenuItems.length > 0;
                  const isExpanded = expandedSections[sectionId] || false;

                  // Group items by parent
                  const itemsByParent = {};
                  sectionMenuItems.forEach((key) => {
                    const menuItem = sideBarData[key];
                    const parent = menuItem.parent || "root";
                    if (!itemsByParent[parent]) {
                      itemsByParent[parent] = [];
                    }
                    itemsByParent[parent].push(key);
                  });

                  // Get root items (those without parent)
                  const rootItems = itemsByParent["root"] || [];

                  return (
                    <Box key={sectionId} sx={{ position: "relative" }}>
                      {/* Main Section Button */}
                      <ListItem
                        disablePadding
                        sx={{
                          display: "block",
                        }}
                      >
                        <ListItemButton
                          onClick={() => {
                            if (!section.title) return;

                            // Check if this section has a direct component
                            if (section.component) {
                              // If it's a locked section, show no access
                              if (isLockedSection) {
                                navigate("noAccess", {
                                  lockedSection: sectionId,
                                  sectionTitle:
                                    section?.title ||
                                    formatSectionTitle(sectionId),
                                });
                                return;
                              }

                              // Check section permission for direct component sections
                              const hasSectionPermission = !section.permission || checkPermission(section.permission, userPermissions);
                              if (!hasSectionPermission) {
                                navigate("noAccess", {
                                  lockedSection: sectionId,
                                  sectionTitle:
                                    section?.title ||
                                    formatSectionTitle(sectionId),
                                });
                                return;
                              }

                              // Clear any active group when going to a direct section
                              setActiveGroup("");

                              // Clear expanded nested states
                              setExpandedNested({});

                              // Update active section
                              setActiveSection(sectionId);
                              localStorage.setItem("activeSection", sectionId);

                              // Use the direct section key
                              const directKey = `section_direct_${sectionId}`;

                              // Update state and localStorage
                              setSelectedComponentKey(directKey);
                              localStorage.setItem("activePage", directKey);
                              localStorage.removeItem("activePageParams");

                              // Update browser history
                              window.history.pushState(
                                {
                                  componentKey: directKey,
                                  params: { sectionId },
                                },
                                "",
                                window.location.pathname,
                              );
                              return;
                            }

                            if (hasSubItems) {
                              toggleSection(sectionId);
                            } else {
                              onMainDrawerClick(isActive, sectionId);
                            }
                          }}
                          sx={{
                            px: 0,
                            py: 0.7,
                            "&:hover": {
                              backgroundColor: "rgba(99, 102, 241, 0.04)",
                            },
                            // Show grey background for active section, even if locked
                            backgroundColor:
                              isActive && !hasSubItems
                                ? "#F3F3F3"
                                : "transparent",
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 36,
                              color:
                                isActive ? "#6366F1" : "#64748B",
                              fontSize: 20,
                            }}
                          >
                            <SidebarIcon
                              isActive={isActive}
                              sectionId={sectionId}
                            >
                              <section.icon />
                            </SidebarIcon>
                          </ListItemIcon>

                          <ListItemText
                            primary={
                              section?.title || formatSectionTitle(sectionId)
                            }
                            primaryTypographyProps={{
                              fontSize: "14px",
                              fontWeight: isActive ? 600 : 500,
                              color: "#232323",
                              textTransform:
                                section?.title === "rcs_bot_create"
                                  ? "uppercase"
                                  : "capitalize",
                              sx: {
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              },
                            }}
                            sx={{
                              flex: 1,
                              ml: 0.5,
                              overflow: "hidden",
                            }}
                          />

                          {/* Show lock icon for locked sections */}
                          {isLockedSection && (
                            <Tooltip
                              title="You don't have permission to access this section"
                              placement="right"
                            >
                              <LockIcon
                                sx={{ fontSize: 16, color: "#FF6B6B", ml: 0.5 }}
                              />
                            </Tooltip>
                          )}

                          {/* Show chevron if there are subitems */}
                          {hasSubItems && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                color: "#111827",
                                transform: isExpanded
                                  ? "rotate(90deg)"
                                  : "none",
                                transition: "transform 0.2s",
                              }}
                            >
                              <ChevronRight sx={{ fontSize: 18 }} />
                            </Box>
                          )}
                        </ListItemButton>
                      </ListItem>

                      {/* Submenu Items with Vertical Line */}
                      {hasSubItems && (
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ position: "relative" }}>
                            {/* Grey vertical line */}
                            <Box
                              sx={{
                                position: "absolute",
                                left: "18px",
                                top: 0,
                                bottom: 0,
                                width: "2px",
                                backgroundColor: "#E5E7EB",
                                borderRadius: "1px",
                              }}
                            />

                            <List
                              component="div"
                              disablePadding
                              sx={{
                                pl: 2.2,
                                pr: 0,
                                position: "relative",
                              }}
                            >
                              {/* Render root items first */}
                              {rootItems.map((itemKey) => {
                                const menuItem = sideBarData[itemKey];
                                const hasNestedItems = menuItem?.hasChildren;
                                const nestedItems = hasNestedItems
                                  ? itemsByParent[itemKey] || []
                                  : [];

                                const isSubItemActive =
                                  selectedComponentKey === itemKey ||
                                  (menuItem.group &&
                                    activeGroup === menuItem.group) ||
                                  nestedItems.some(
                                    (key) => selectedComponentKey === key,
                                  );

                                // Check if this specific item should show a lock (based on accessibility)
                                const showLock = shouldShowLock(itemKey) || isLockedSection;

                                return (
                                  <Box key={itemKey}>
                                    <ListItem
                                      disablePadding
                                      sx={{
                                        display: "block",
                                        position: "relative",
                                      }}
                                    >
                                      <ListItemButton
                                        onClick={() => {
                                          if (hasNestedItems) {
                                            toggleNestedItems(itemKey);
                                          } else {
                                            handleListItemClick(itemKey);
                                          }
                                        }}
                                        sx={{
                                          px: 2,
                                          pr: 0,
                                          py: "5px",
                                          width: "100%",
                                          "&:hover": {
                                            backgroundColor:
                                              menuItem.id === "wa_analytics"
                                                ? "transparent"
                                                : isSubItemActive && !showLock
                                                  ? ColorsConstants.primaryBlue100
                                                  : "transparent",
                                          },
                                          backgroundColor:
                                            menuItem.id === "wa_analytics" &&
                                            isSubItemActive
                                              ? "transparent"
                                              : isSubItemActive && !showLock
                                                ? ColorsConstants.primaryBlue100
                                                : "transparent",
                                          position: "relative",
                                          zIndex: 1,
                                        }}
                                      >
                                        <Tooltip
                                          title={menuItem.title}
                                          placement="right"
                                          arrow
                                        >
                                          <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                                            <ListItemText
                                              primary={menuItem.title}
                                              primaryTypographyProps={{
                                                fontSize: "13px",
                                                fontWeight: 400,
                                                color:
                                                  menuItem.id ===
                                                    "wa_analytics" &&
                                                  isSubItemActive
                                                    ? "#3C3C3C"
                                                    : isSubItemActive && !showLock
                                                      ? ColorsConstants.primaryWhite
                                                      : "#3C3C3C",
                                                sx: {
                                                  overflow: "hidden",
                                                  textOverflow: "ellipsis",
                                                  whiteSpace: "nowrap",
                                                },
                                              }}
                                              sx={{
                                                ml: 0.5,
                                                overflow: "hidden",
                                                flex: 1,
                                              }}
                                            />
                                            
                                            {/* Show lock icon for locked items */}
                                            {showLock && (
                                              <Tooltip
                                                title="You don't have permission to access this item"
                                                placement="right"
                                              >
                                                <LockIcon
                                                  sx={{ 
                                                    fontSize: 14, 
                                                    color: "#FF6B6B", 
                                                    ml: 0.5 
                                                  }}
                                                />
                                              </Tooltip>
                                            )}
                                          </Box>
                                        </Tooltip>

                                        {/* Show chevron if has nested items */}
                                        {hasNestedItems && (
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              color: "#111827",
                                              transform: expandedNested[itemKey]
                                                ? "rotate(90deg)"
                                                : "none",
                                              transition: "transform 0.2s",
                                            }}
                                          >
                                            <ChevronRight
                                              sx={{ fontSize: 18 }}
                                            />
                                          </Box>
                                        )}
                                      </ListItemButton>
                                    </ListItem>

                                    {/* Render nested items if expanded */}
                                    {hasNestedItems &&
                                      expandedNested[itemKey] && (
                                        <List
                                          component="div"
                                          disablePadding
                                          sx={{ pl: 2 }}
                                        >
                                          {nestedItems.map((nestedKey) => {
                                            const nestedItem =
                                              sideBarData[nestedKey];
                                            const isNestedActive =
                                              selectedComponentKey ===
                                              nestedKey;
                                            
                                            // Check if this nested item should show a lock (based on accessibility)
                                            const showNestedLock = shouldShowLock(nestedKey) || isLockedSection;

                                            return (
                                              <ListItem
                                                key={nestedKey}
                                                disablePadding
                                                sx={{
                                                  display: "block",
                                                  position: "relative",
                                                }}
                                              >
                                                <ListItemButton
                                                  onClick={() =>
                                                    handleListItemClick(
                                                      nestedKey,
                                                    )
                                                  }
                                                  sx={{
                                                    px: 2,
                                                    py: "5px",
                                                    width: "100%",
                                                    "&:hover": {
                                                      backgroundColor:
                                                        isNestedActive && !showNestedLock
                                                          ? ColorsConstants.primaryBlue100
                                                          : "transparent",
                                                    },
                                                    backgroundColor:
                                                      isNestedActive && !showNestedLock
                                                        ? ColorsConstants.primaryBlue100
                                                        : "transparent",
                                                    position: "relative",
                                                    zIndex: 1,
                                                  }}
                                                >
                                                  <Tooltip
                                                    title={nestedItem.title}
                                                    placement="right"
                                                    arrow
                                                  >
                                                    <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                                                      <ListItemText
                                                        primary={nestedItem.title}
                                                        primaryTypographyProps={{
                                                          fontSize: "12px",
                                                          fontWeight:
                                                            isNestedActive && !showNestedLock
                                                              ? 500
                                                              : 400,
                                                          color: isNestedActive && !showNestedLock
                                                            ? ColorsConstants.primaryWhite
                                                            : "#3C3C3C",
                                                          sx: {
                                                            overflow: "hidden",
                                                            textOverflow:
                                                              "ellipsis",
                                                            whiteSpace: "nowrap",
                                                          },
                                                        }}
                                                        sx={{
                                                          ml: 1,
                                                          overflow: "hidden",
                                                          flex: 1,
                                                        }}
                                                      />
                                                      
                                                      {/* Show lock icon for locked nested items */}
                                                      {showNestedLock && (
                                                        <Tooltip
                                                          title="You don't have permission to access this item"
                                                          placement="right"
                                                        >
                                                          <LockIcon
                                                            sx={{ 
                                                              fontSize: 12, 
                                                              color: "#FF6B6B", 
                                                              ml: 0.5 
                                                            }}
                                                          />
                                                        </Tooltip>
                                                      )}
                                                    </Box>
                                                  </Tooltip>
                                                </ListItemButton>
                                              </ListItem>
                                            );
                                          })}
                                        </List>
                                      )}
                                  </Box>
                                );
                              })}
                            </List>
                          </Box>
                        </Collapse>
                      )}
                    </Box>
                  );
                })}
              </Box>
            );
          })}
        </Box>
      </Drawer>

      {/* Main Content Area - Header + Page Content */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          width: `calc(100% - ${DRAWER_WIDTH}px)`,
          height: "100vh",
          overflow: "hidden",
        }}
      >
        {/* Header - Positioned next to sidebar */}
        <Box sx={{ flexShrink: 0, width: "100%" }}>
          <Header drawerOpen={drawerOpen} setDrawerOpen={setDrawerOpen} />
        </Box>

        {/* Scrollable Content Area */}
        {/* Main content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            overflowY: "auto !important",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ flexGrow: 1  }}>
            {ComponentToRender && (
              <ComponentToRender
                onNavigate={handleListItemClick}
                pageParams={pageParams}
              />
            )}
          </Box>

          {/* Footer - sticky when content is short, pushed down when content is tall */}
          <Box sx={{ flexShrink: 0, mt: "auto" }}>
            <Footer />
          </Box>
</Box>
      </Box>
    </Box>
  );
}